/* The product form: the size picker, the quantity steps and rules, subscription plans and the price, all following Shopify's variant select. Loaded by the product section only, ahead of the other product scripts that listen to it. */
class C4Product extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    this.variant = this.querySelector('[data-variant]');
    this.plan = this.querySelector('[data-selling-plan]');
    const quantity = this.querySelector('[name="quantity"]');
    const steps = [...this.querySelectorAll('[data-product-step]')];
    const syncQuantity = () =>
      steps.forEach((button) => {
        button.hidden = false;
        button.disabled =
          Number(button.dataset.productStep) < 0
            ? Number(quantity.value) <= Number(quantity.min || 1)
            : Boolean(quantity.max) && Number(quantity.value) >= Number(quantity.max);
      });
    this.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest('[data-product-step]');
        if (!button) return;
        quantity.stepUp(Number(button.dataset.productStep));
        quantity.dispatchEvent(new Event('change', { bubbles: true }));
      },
      { signal: this.abort.signal },
    );
    quantity.addEventListener('change', syncQuantity, { signal: this.abort.signal });
    const update = () => {
      const option = this.variant.selectedOptions[0];
      this.querySelector('[data-variant-summary]').textContent = option.dataset.title;
      this.querySelector('[name="id"]').value = option.value;
      const quantity = this.querySelector('[name="quantity"]');
      quantity.min = option.dataset.min;
      quantity.step = option.dataset.step;
      if (option.dataset.max) quantity.max = option.dataset.max;
      else quantity.removeAttribute('max');
      if (!quantity.checkValidity()) quantity.value = quantity.min;
      syncQuantity();
      const previousPlan = this.plan.value;
      const template = [...this.querySelectorAll('[data-plans-for]')].find(
        (node) => node.dataset.plansFor === option.value,
      );
      this.plan.replaceChildren(template.content.cloneNode(true));
      if ([...this.plan.options].some((plan) => plan.value === previousPlan))
        this.plan.value = previousPlan;
      const hasPlans = [...this.plan.options].some((plan) => plan.value !== '');
      this.plan.disabled = !hasPlans;
      this.querySelector('[data-plan-field]').hidden = !hasPlans;
      const button = this.querySelector('[data-add-label]');
      button.disabled =
        option.dataset.available !== 'true' ||
        (option.dataset.requiresPlan === 'true' && !hasPlans);
      button.textContent = button.disabled ? button.dataset.soldOutLabel : button.dataset.addLabel;
      this.updatePrice();
      this.availability(option);
    };
    this.addEventListener(
      'change',
      (event) => {
        if (event.target === this.variant) {
          update();
          const url = new URL(location.href);
          url.searchParams.set('variant', this.variant.value);
          history.replaceState({}, '', url);
        } else if (event.target === this.plan) this.updatePrice();
      },
      { signal: this.abort.signal },
    );
    /* Pages that open in the drawer start loading on intent: a pointer resting on the link (65ms, so passing over
       doesn't count), focus, or a touch. */
    let intent;
    const warm = (event) => {
      const link = event.target.closest?.('[data-drawer-page]');
      if (link) this.loadPage(link.href).catch(() => {});
    };
    document.addEventListener(
      'pointerover',
      (event) => {
        clearTimeout(intent);
        if (event.target.closest?.('[data-drawer-page]'))
          intent = setTimeout(() => warm(event), 65);
      },
      { signal: this.abort.signal },
    );
    document.addEventListener('pointerout', () => clearTimeout(intent), {
      signal: this.abort.signal,
    });
    document.addEventListener('focusin', warm, { signal: this.abort.signal });
    document.addEventListener('touchstart', warm, { passive: true, signal: this.abort.signal });
    /* Book a demo, Talk to us and the pills open the enquiry over the page, that request chosen and, for a build,
       the setup written in. Without the script they are links to the enquiry in the page. */
    document.addEventListener(
      'click',
      (event) => {
        const info = event.target.closest('[data-info]');
        if (info) return this.info(info, event);
        const page = event.target.closest('[data-drawer-page]');
        if (page) return this.page(page, event);
        const link = event.target.closest('[data-enquire]');
        if (link) this.enquire(link, event);
      },
      { signal: this.abort.signal },
    );
    /* Back from sending the enquiry: its thanks (or its errors) in the drawer again. Shopify marks every contact form
       on the page as sent after any one is (the trade application too), so this one remembers it was the one. */
    const panel = document.querySelector('[data-enquiry]');
    let sent = false;
    try {
      sent = sessionStorage.getItem('c4-enquiry-sent') === '1';
      sessionStorage.removeItem('c4-enquiry-sent');
    } catch {
      /* Without storage it stays in the page, open. */
    }
    panel?.closest('form')?.addEventListener(
      'submit',
      () => {
        try {
          sessionStorage.setItem('c4-enquiry-sent', '1');
        } catch {
          /* Nothing to remember it in. */
        }
      },
      { signal: this.abort.signal },
    );
    if (panel && sent) requestAnimationFrame(() => this.enquire(null, null));
    this.topBar();
    this.switcher();
    this.stage();
    this.querySelector('[data-variant-submit]').hidden = true;
    this.querySelector('[data-variant-form]').addEventListener(
      'submit',
      (event) => {
        event.preventDefault();
        update();
      },
      { signal: this.abort.signal },
    );
    update();
  }
  /*
    Where the chosen finish stands (snippets/variant-availability.liquid prints it on each variant's option): the
    words in every [data-availability] place on the page, and the arrival dates, worked out here from today in
    working days (a weekend, or an order after the dispatch cut-off, starts from the next working day). In stock and
    sent within a working day (data-quick), the day it ships is named: Ships today, Ships tomorrow, Ships Monday.
  */
  availability(option) {
    const data = option?.dataset;
    if (!data?.state) return;
    document.querySelectorAll('[data-availability]').forEach((root) => {
      root.dataset.state = data.state;
      root.dataset.sameDay = data.sameDay;
      const when = data.quick === 'true' ? this.shipsWhen(root) : '';
      const words = {
        ships: when || data.ships,
        dispatch: when,
        hint: data.hint,
        note: data.shipsNote,
        pickup: data.pickup,
        'pickup-short': data.pickupShort,
        dates: this.dates(data, root),
      };
      root.querySelectorAll('[data-avail]').forEach((node) => {
        const text = words[node.dataset.avail] || '';
        node.textContent = text;
        if (node.matches('dd, p, b')) node.hidden = !text;
        const item = node.closest('li');
        if (item && root.contains(item)) item.hidden = !text;
      });
      root.querySelectorAll('[data-avail-stores]').forEach((list) => this.stores(list, data));
      root.querySelectorAll('[data-avail-when]').forEach((node) => {
        node.hidden =
          node.dataset.availWhen === 'same-day'
            ? data.sameDay !== 'true'
            : node.dataset.availWhen !== data.state;
      });
    });
  }
  /* Check availability: each pickup location for this finish, there now or how soon it can be. */
  stores(list, data) {
    let places = [];
    try {
      places = JSON.parse(data.stores || '[]');
    } catch {
      places = [];
    }
    const available = list.dataset.availableLabel || 'Available';
    if (!places.length) {
      const only = list.querySelector('li');
      if (!only) return;
      only.dataset.here = String(data.state === 'store');
      only.lastElementChild.textContent = data.state === 'store' ? available : data.away;
      return;
    }
    list.replaceChildren(
      ...places.map((place) => {
        const item = document.createElement('li');
        item.dataset.here = String(place.available);
        const where = document.createElement('span');
        const name = document.createElement('b');
        name.textContent = place.name;
        where.append(name, place.town ? ` ${place.town}` : '');
        const status = document.createElement('span');
        status.textContent = place.available
          ? [available, place.time].filter(Boolean).join('. ')
          : data.away;
        item.append(where, status);
        return item;
      }),
    );
  }
  /*
    The day we dispatch, on New Zealand's clock whatever the shopper's: today on a working day before the cut-off
    hour, else the next working day (no weekends, nor the days we don't dispatch: Theme settings > Delivery and pickup).
    Days are dates at UTC midnight, so counting them never crosses a time zone or daylight saving.
  */
  dispatch(root) {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-NZ', {
        timeZone: 'Pacific/Auckland',
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        hourCycle: 'h23',
      })
        .formatToParts(new Date())
        .map((part) => [part.type, part.value]),
    );
    const today = new Date(
      Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day)),
    );
    const closed = (root.dataset.closed || '').split(/[\s,]+/).filter(Boolean);
    const off = (day) =>
      day.getUTCDay() === 0 ||
      day.getUTCDay() === 6 ||
      closed.includes(day.toISOString().slice(0, 10));
    const start = new Date(today);
    if (Number(parts.hour) >= Number(root.dataset.cutoff || 12) || off(start)) {
      do start.setUTCDate(start.getUTCDate() + 1);
      while (off(start));
    }
    return { today, start, off };
  }
  /* Ships today, Ships tomorrow, Ships Monday; after a longer close, the date (Ships Mon 4 Jan). */
  shipsWhen(root) {
    const { today, start } = this.dispatch(root);
    const days = Math.round((start - today) / 864e5);
    if (days === 0) return root.dataset.shipToday || 'Ships today';
    if (days === 1) return root.dataset.shipTomorrow || 'Ships tomorrow';
    const day = start
      .toLocaleDateString(
        'en-NZ',
        days < 7
          ? { weekday: 'long', timeZone: 'UTC' }
          : { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' },
      )
      .replace(',', '');
    return (root.dataset.shipDay || 'Ships [day]').replace('[day]', day);
  }
  dates(data, root) {
    /* Sent within a working day, the day it ships is the promise; courier times aren't ours to date. */
    if (!data.daysMin || !data.daysMax || data.quick === 'true') return '';
    const { start, off } = this.dispatch(root);
    const after = (days) => {
      const day = new Date(start);
      for (let left = Number(days); left > 0;) {
        day.setUTCDate(day.getUTCDate() + 1);
        if (!off(day)) left -= 1;
      }
      return day
        .toLocaleDateString('en-NZ', {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          timeZone: 'UTC',
        })
        .replace(',', '');
    };
    const from = after(data.daysMin);
    const to = after(data.daysMax);
    return from === to
      ? (root.dataset.arrivesOne || '[date]').replace('[date]', from)
      : (root.dataset.arrives || '[from] – [to]').replace('[from]', from).replace('[to]', to);
  }
  /*
    The product drawer (sections/main-product.liquid): a side panel (assets/side-panel.js), sliding in from the right
    and pushing the page, for the enquiry and the delivery details. One thing in it at a time; the panel closes it on
    its button, Escape or a click beside it, and gives focus back to what opened it.
  */
  drawer(content, opener, label) {
    const drawer = document.getElementById('ProductDrawer');
    if (!drawer?.showModal) return false;
    if (label) drawer.setAttribute('aria-label', label);
    drawer.querySelector('[data-drawer-body]').replaceChildren(content);
    if (window.C4SidePanel) window.C4SidePanel.open(drawer, { opener });
    else if (!drawer.open) drawer.showModal();
    return true;
  }
  /* Get delivery dates: the dates and pickup for the chosen finish, in the drawer. */
  info(link, event) {
    const template = document.querySelector(`template[data-info-template="${link.dataset.info}"]`);
    if (!template) return;
    const content = document.createElement('div');
    content.className = 'product-drawer-info';
    content.append(template.content.cloneNode(true));
    const label = content.querySelector('h2')?.textContent.trim();
    if (!this.drawer(content, link, label)) return;
    event.preventDefault();
    this.availability(this.variant.selectedOptions[0]);
  }
  /* A page of ours in the drawer (a producer's, a variety's or a processing method's, from the specs grid or a link
     in the drawer): its [data-drawer-content], fetched once and kept. The fetch starts on intent (pointing at the
     link for a moment, focusing it, touching it), so it's usually there by the click, and the page's first photos
     are fetched with it. A click that still has to wait opens the drawer at once, quietly loading, and fills it when
     the page arrives. Should the fetch fail, the link goes to the page. */
  loadPage(url) {
    this.pages ||= new Map();
    if (!this.pages.has(url))
      this.pages.set(
        url,
        fetch(url)
          .then((response) => (response.ok ? response.text() : Promise.reject()))
          .then((html) => {
            const part = new DOMParser()
              .parseFromString(html, 'text/html')
              .querySelector('[data-drawer-content]');
            if (!part) return Promise.reject();
            part.querySelectorAll('[data-drawer-skip], script').forEach((el) => el.remove());
            part.querySelectorAll('h1').forEach((h1) => {
              const h2 = document.createElement('h2');
              h2.className = h1.className;
              h2.append(...h1.childNodes);
              h1.replaceWith(h2);
            });
            part.querySelectorAll('img').forEach((img, index) => {
              if (index > 1) return;
              const warm = new Image();
              if (img.sizes) warm.sizes = img.sizes;
              if (img.srcset) warm.srcset = img.srcset;
              warm.src = img.src;
            });
            return part;
          })
          .catch((error) => {
            this.pages.delete(url);
            return Promise.reject(error);
          }),
      );
    return this.pages.get(url);
  }
  page(link, event) {
    event.preventDefault();
    const url = link.href;
    let shown = false;
    const wait = setTimeout(() => {
      const loading = document.createElement('div');
      loading.className = 'product-drawer-info product-drawer-page drawer-loading';
      loading.setAttribute('aria-busy', 'true');
      loading.innerHTML = '<span></span><span></span><span></span>';
      shown = this.drawer(
        loading,
        link,
        link.querySelector('.visually-hidden')?.textContent.trim(),
      );
    }, 120);
    this.loadPage(url)
      .then((part) => {
        clearTimeout(wait);
        /* Closed while it was on its way: leave it closed. */
        if (shown && !document.getElementById('ProductDrawer')?.open) return;
        const content = document.createElement('div');
        content.className = 'product-drawer-info product-drawer-page';
        content.append(part.cloneNode(true));
        if (!this.drawer(content, link, content.querySelector('h2')?.textContent.trim()) && !shown)
          location.href = url;
      })
      .catch(() => {
        clearTimeout(wait);
        location.href = url;
      });
  }
  /* Talk to a specialist, Book a demo, Get a quote: the enquiry in the drawer, that request chosen and, for a
     build, the setup written in. */
  enquire(link, event) {
    const panel = (this.enquiryForm || document).querySelector('[data-enquiry]');
    const form = this.enquiryForm || panel?.closest('form');
    if (!form) return;
    this.enquiryForm = form;
    const label = panel.querySelector('summary')?.textContent.trim();
    if (!this.drawer(form, link, label)) return;
    event?.preventDefault();
    panel.open = true;
    const type = panel.querySelector('[data-enquiry-type]');
    if (link && [...(type?.options || [])].some((option) => option.value === link.dataset.enquire))
      type.value = link.dataset.enquire;
    const body = panel.querySelector('[data-enquiry-body]');
    const text = this.querySelector('c4-product-build')?.summaryText?.();
    if (body && text && (!body.value || body.value === body.dataset.filled))
      body.value = body.dataset.filled = `${text}\n\n`;
    panel.querySelector('input:not([type="hidden"])')?.focus({ preventScroll: true });
  }
  updatePrice() {
    this.querySelector('[data-product-price]').textContent =
      this.plan.selectedOptions[0]?.dataset.price || this.variant.selectedOptions[0].dataset.price;
  }
  /*
    The product bar (sections/main-product.liquid): on wide screens, once the page's own Add button (a build's name,
    where the build has no Add button in the column) has scrolled under the navigation, it shows at the top, the one
    sticky way to buy there. It mirrors the Add button: its total, its label when sold out, its disabled state.
  */
  topBar() {
    const bar = document.querySelector('[data-product-bar]');
    if (!bar) return;
    /* Fixed to the view, so out of <main>, whose transform (the bag's push) would carry it with the page. */
    if (bar.parentElement !== document.body) document.body.append(bar);
    this.bar = bar;
    const button = this.querySelector('[data-add-label]');
    const add = bar.querySelector('[data-bar-add]');
    const build = this.classList.contains('c4-product--build');
    const trigger = build ? this.querySelector('.product-heading h1') : button;
    const wide = matchMedia('(min-width: 1024px)');
    let past = false;
    const show = () => {
      const shown = wide.matches && past;
      bar.hidden = false;
      bar.toggleAttribute('data-shown', shown);
      bar.inert = !shown;
    };
    const mirror = () => {
      if (!button || !add) return;
      add.disabled = button.disabled;
      if (!build) {
        add.textContent = button.disabled ? button.dataset.soldOutLabel : button.dataset.addLabel;
        const total = bar.querySelector('[data-bar-total]');
        if (total && button.dataset.total) total.textContent = button.dataset.total;
        const line = bar.querySelector('[data-bar-line]');
        const option = this.variant.selectedOptions[0];
        if (line && option && this.variant.options.length > 1)
          line.textContent = option.dataset.title;
      }
    };
    /* Past means above the navigation's line. Checked once a frame while scrolling, and only the attribute changes
       when the state does (an observer misses a jump straight past it, as from Home to End). */
    if (trigger) {
      let queued = false;
      const check = () => {
        queued = false;
        const now = trigger.getBoundingClientRect().bottom < 96;
        if (now !== past) {
          past = now;
          show();
        }
      };
      const schedule = () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(check);
      };
      addEventListener('scroll', schedule, { passive: true, signal: this.abort.signal });
      addEventListener('resize', schedule, { passive: true, signal: this.abort.signal });
      check();
    }
    if (button) {
      new MutationObserver(mirror).observe(button, {
        attributes: true,
        attributeFilter: ['data-total', 'disabled'],
      });
      mirror();
    }
    wide.addEventListener('change', show, { signal: this.abort.signal });
    show();
  }
  /*
    The floating bar's quick switcher: the first option (a finish, a size) as a chip and its name, opening the other
    values as chips or words. Choosing one sets that option's own picker, so the variant, price, photo and
    availability follow exactly as if it were chosen above.
  */
  switcher() {
    const box = this.querySelector('[data-buy-switch]');
    const signal = this.abort.signal;
    const line = this.querySelector('[data-buy-line]');
    const build = this.classList.contains('c4-product--build');
    const strip = (text = '') =>
      box?.dataset.strip ? text.replace(box.dataset.strip, '').trim() || text : text;
    if (line && !build)
      this.variant.addEventListener(
        'change',
        () => {
          line.textContent = strip(this.variant.selectedOptions[0]?.dataset.title);
        },
        { signal },
      );
    if (!box) return;
    const toggle = box.querySelector('.buy-switch-toggle');
    const list = box.querySelector('.buy-switch-list');
    const index = Number(box.dataset.index) - 1;
    const set = (open) => {
      list.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
    };
    toggle.addEventListener('click', () => set(list.hidden), { signal });
    list.addEventListener(
      'click',
      (event) => {
        const choice = event.target.closest('[data-switch-value]');
        if (!choice) return;
        const value = choice.dataset.switchValue;
        const picker = this.querySelectorAll('[data-product-option]')[index];
        if (picker && !picker.closest('[hidden]')) {
          picker.value = value;
          picker.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          const match = [...this.variant.options].find(
            (option) => JSON.parse(option.dataset.options || '[]')[index] === value,
          );
          if (match) {
            this.variant.value = match.value;
            this.variant.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
        set(false);
        toggle.focus();
      },
      { signal },
    );
    document.addEventListener(
      'click',
      (event) => {
        if (!box.contains(event.target)) set(false);
      },
      { signal },
    );
    box.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && !list.hidden) {
          set(false);
          toggle.focus();
        }
      },
      { signal },
    );
    const sync = () => {
      const value = JSON.parse(this.variant.selectedOptions[0]?.dataset.options || '[]')[index];
      box.querySelector('[data-switch-name]').textContent = strip(value);
      list.querySelectorAll('[data-switch-value]').forEach((choice) => {
        const chosen = choice.dataset.switchValue === value;
        choice.setAttribute('aria-pressed', String(chosen));
        const chip = box.querySelector('[data-switch-chip]');
        const own = choice.querySelector('.buy-switch-chip');
        if (chosen && chip && own) chip.setAttribute('style', own.getAttribute('style'));
      });
    };
    this.variant.addEventListener('change', sync, { signal });
    sync();
  }
  /*
    The stage (sections/main-product.liquid): on two columns, what the right column is talking about takes the
    picture's place while it's read. Anything in the column marked [data-stage] takes part: its own media (a
    template[data-stage-media]: a build step's, an option's or a Product story block's), else for a build step the
    product chosen in it; "photos" (Model, the product's options) gives the photos back, which follow the variant. The read line
    is just above the middle of the view. Photos brings them back until the next part.
    Each thing shown is a layer in the frame. A part's own media (a video) is kept once made: it starts loading a
    screen before its part is reached, plays while read, and pauses, still loaded, when the stage moves on.
  */
  stage() {
    const stage = this.closest('section')?.querySelector('[data-stage-view]');
    const parts = [...this.querySelectorAll('[data-stage]')];
    if (!stage || !parts.length || !('IntersectionObserver' in window)) return;
    const signal = this.abort.signal;
    const wide = matchMedia('(min-width: 800px)');
    const frame = stage.querySelector('[data-stage-frame]');
    const caption = stage.querySelector('[data-stage-caption]');
    let active = null;
    let dismissed = null;
    const kept = new Map();
    const layer = (content) => {
      let node = kept.get(content.key);
      if (node) return node;
      node = document.createElement('div');
      node.className = 'stage-layer';
      node.append(content.make());
      frame.append(node);
      if (content.keep) kept.set(content.key, node);
      return node;
    };
    const render = () => {
      const content =
        wide.matches && active && active !== dismissed ? this.stageContent(active) : null;
      const key = content?.key || '';
      if (stage.dataset.key === key) return;
      stage.dataset.key = key;
      const shown = content ? layer(content) : null;
      [...frame.children].forEach((node) => {
        const on = node === shown;
        node.toggleAttribute('data-active', on);
        node
          .querySelectorAll('c4-video')
          .forEach((video) => (on ? video.play?.() : video.pause?.()));
        if (!on && ![...kept.values()].includes(node)) node.remove();
      });
      if (!content) {
        stage.removeAttribute('data-shown');
        return;
      }
      caption.textContent = content.caption || '';
      stage.setAttribute('data-shown', '');
      follow();
    };
    /*
      A part marked data-stage-scroll (a coffee's origin) moves its stage as it is read: its progress through the
      view, 0 as it arrives to 1 as it leaves, turns the land round the farm (assets/terrain.js eases to it).
    */
    const follow = () => {
      if (!active?.hasAttribute('data-stage-scroll') || !wide.matches) return;
      const box = active.getBoundingClientRect();
      const progress = (innerHeight * 0.75 - box.top) / (box.height + innerHeight * 0.5);
      kept.get(`media:${active.dataset.stageKey}`)?.querySelector('c4-terrain')?.turn?.(progress);
    };
    /*
      Which part is read, decided in one place, once a frame as the page scrolls: the last part whose top has passed
      the read line, just under the middle of the view (a gap between parts keeps the one before, as Apple's stage
      does). Near the foot of the page the line slides smoothly down the view, so the last parts of a short column
      are read without a jump at the very end. Above the first part the photos come back, with a little give so a
      part on the line doesn't flicker between the two.
    */
    let queued = false;
    const decide = () => {
      queued = false;
      if (!wide.matches) return;
      const base = innerHeight * 0.45;
      const left = document.documentElement.scrollHeight - (scrollY + innerHeight);
      const near = innerHeight * 0.5;
      const line =
        left < near ? base + (innerHeight * 0.85 - base) * (1 - Math.max(0, left) / near) : base;
      let next = null;
      for (const part of parts) {
        if (part.getBoundingClientRect().top <= line) next = part;
        else break;
      }
      if (!next && active === parts[0] && parts[0].getBoundingClientRect().top <= line + 32)
        next = active;
      if (next !== active) {
        active = next;
        render();
      }
      follow();
    };
    const schedule = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(decide);
    };
    addEventListener('scroll', schedule, { passive: true, signal });
    addEventListener('resize', schedule, { passive: true, signal });
    /* A screen ahead, a part's own video or land starts loading, out of sight. */
    const ahead = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (!entry.isIntersecting || !wide.matches) return;
          const content = this.stageContent(entry.target);
          if (content?.keep) layer(content);
          ahead.unobserve(entry.target);
        }),
      { rootMargin: '100% 0px' },
    );
    parts
      .filter((part) =>
        part
          .querySelector(':scope > template[data-stage-media]')
          ?.content.querySelector('c4-video, c4-terrain'),
      )
      .forEach((part) => ahead.observe(part));
    this.aheadObserver = ahead;
    schedule();
    this.addEventListener('change', () => requestAnimationFrame(render), { signal });
    stage.querySelector('[data-stage-back]')?.addEventListener(
      'click',
      () => {
        dismissed = active;
        render();
      },
      { signal },
    );
    wide.addEventListener('change', render, { signal });
  }
  stageContent(part) {
    const image = (src, alt = '') => {
      const img = document.createElement('img');
      img.src = src;
      img.alt = alt;
      img.decoding = 'async';
      return img;
    };
    const own = part.querySelector(':scope > template[data-stage-media]');
    if (own)
      return {
        key: `media:${part.dataset.stageKey}`,
        make: () => own.content.cloneNode(true),
        keep: true,
        caption: part.dataset.stageCaption,
      };
    if (part.dataset.stage === 'choice') {
      const input = part.querySelector('input:checked[data-stage-image]');
      if (!input) return null;
      const option = input.closest('.build-card')?.querySelector('[data-build-variant]')
        ?.selectedOptions[0];
      const src = option?.dataset.image || input.dataset.stageImage;
      if (!src) return null;
      return {
        key: `choice:${src}`,
        make: () => image(src, input.dataset.title),
        caption: [input.dataset.title, option?.dataset.title].filter(Boolean).join(', '),
      };
    }
    return null;
  }
  disconnectedCallback() {
    this.stageObserver?.disconnect();
    this.aheadObserver?.disconnect();
    this.abort?.abort();
    /* A theme editor reload renders a new bar in the section. */
    this.bar?.remove();
  }
}
/*
  A video in a picture's place (snippets/video-embed.liquid), cropped to its box like a photo: an uploaded video, or
  a YouTube or Vimeo link. Ambient: muted, no controls, looping (a link from its start time to its end), with a glass
  chip for the sound and YouTube's captions (off to start: a video may have its own). It plays when asked: the stage
  plays the one being read and pauses the rest, which stay loaded; anywhere else it plays while in view. With reduced
  motion, or in play mode, it waits for the play button and plays with sound and controls.
*/
class C4Video extends HTMLElement {
  static parse(href) {
    let url;
    try {
      url = new URL(href);
    } catch {
      return null;
    }
    const host = url.hostname.replace(/^(www|m)\./, '');
    const seconds = (value) => {
      if (!value) return 0;
      if (/^\d+$/.test(value)) return Number(value);
      const [, h = 0, m = 0, s = 0] = value.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/) || [];
      return h * 3600 + m * 60 + Number(s);
    };
    const hash = new URLSearchParams(url.hash.slice(1));
    const start = seconds(
      url.searchParams.get('t') || url.searchParams.get('start') || hash.get('t'),
    );
    const end = seconds(url.searchParams.get('end'));
    if (host === 'youtu.be') return { host: 'youtube', id: url.pathname.slice(1), start, end };
    if (host.endsWith('youtube.com') || host.endsWith('youtube-nocookie.com')) {
      const id =
        url.searchParams.get('v') || url.pathname.match(/\/(?:embed|shorts|live)\/([\w-]+)/)?.[1];
      return id ? { host: 'youtube', id, start, end } : null;
    }
    if (host.endsWith('vimeo.com')) {
      const id = url.pathname.match(/(\d{5,})/)?.[1];
      return id ? { host: 'vimeo', id, start, end } : null;
    }
    return null;
  }
  connectedCallback() {
    if (this.set) return;
    this.set = true;
    this.native = this.querySelector(':scope > video');
    this.video = this.native ? null : C4Video.parse(this.dataset.url);
    if (!this.native && !this.video) return;
    this.ambient =
      this.dataset.mode !== 'play' && !matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.muted = true;
    this.captions = false;
    this.wanted = false;
    this.toggleAttribute('data-ambient', this.ambient);
    if (this.native) {
      this.native.controls = !this.ambient;
      this.native.muted = this.ambient;
      this.native.addEventListener('playing', () => this.setAttribute('data-playing', ''));
    } else if (this.ambient) this.draw();
    else
      this.querySelector('.video-embed-play')?.addEventListener('click', (event) => {
        event.preventDefault();
        this.draw();
        this.play();
      });
    if (this.ambient) {
      const controls = this.querySelector('.video-embed-controls');
      if (controls) controls.hidden = false;
      controls
        ?.querySelector('[data-video-sound]')
        ?.addEventListener('click', () => this.sound(this.muted));
      controls
        ?.querySelector('[data-video-captions]')
        ?.addEventListener('click', () => this.caption(!this.captions));
    }
    /* Anywhere but the stage, it plays while it's in view. */
    if (this.ambient && !this.closest('[data-stage-frame]')) {
      this.seen = new IntersectionObserver(
        ([entry]) => (entry.isIntersecting ? this.play() : this.pause()),
        {
          threshold: 0.4,
        },
      );
      this.seen.observe(this);
    }
  }
  draw() {
    const { host, id, start, end } = this.video;
    const ambient = this.ambient;
    const frame = document.createElement('iframe');
    frame.title = this.dataset.title || '';
    frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    frame.referrerPolicy = 'strict-origin-when-cross-origin';
    if (host === 'youtube') {
      const query = new URLSearchParams({
        autoplay: ambient ? 0 : 1,
        mute: ambient ? 1 : 0,
        controls: ambient ? 0 : 1,
        playsinline: 1,
        rel: 0,
        iv_load_policy: 3,
        cc_load_policy: 0,
        disablekb: ambient ? 1 : 0,
        enablejsapi: 1,
        origin: location.origin,
        start,
      });
      if (end) query.set('end', end);
      frame.src = `https://www.youtube-nocookie.com/embed/${id}?${query}`;
    } else {
      const query = new URLSearchParams(
        ambient
          ? {
              autoplay: 0,
              muted: 1,
              loop: 1,
              controls: 0,
              autopause: 0,
              title: 0,
              byline: 0,
              portrait: 0,
            }
          : { autoplay: 1 },
      );
      frame.src = `https://player.vimeo.com/video/${id}?${query}${start ? `#t=${start}s` : ''}`;
    }
    this.frame = frame;
    this.append(frame);
    const origin = new URL(frame.src).origin;
    const post = (message) => frame.contentWindow?.postMessage(JSON.stringify(message), origin);
    this.send =
      host === 'youtube'
        ? (func, ...args) => post({ event: 'command', func, args })
        : (method, value) => post(value === undefined ? { method } : { method, value });
    frame.addEventListener('load', () => {
      if (host === 'youtube') post({ event: 'listening', id: 1, channel: 'widget' });
      else {
        /* Vimeo has no ready message without its script: give it a moment. */
        setTimeout(() => this.ready(), 600);
        post({ method: 'addEventListener', value: 'play' });
      }
    });
    this.listen = (event) => {
      if (event.source !== frame.contentWindow) return;
      let data;
      try {
        data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }
      if (host === 'vimeo') {
        if (data.event === 'play') this.setAttribute('data-playing', '');
        return;
      }
      if (data.event === 'onReady' || data.event === 'initialDelivery') this.ready();
      /* Captions stay as chosen, whenever YouTube loads them (on the first play, after the loop's seek). */
      if (data.event === 'onApiChange' && !this.captions) this.send('unloadModule', 'captions');
      const state = data.event === 'onStateChange' ? data.info : data.info?.playerState;
      if (state === 1) {
        this.setAttribute('data-playing', '');
        this.caption(this.captions);
      }
      if (state === 0 && ambient) {
        this.send('seekTo', start, true);
        this.send('playVideo');
      }
    };
    addEventListener('message', this.listen);
  }
  /* The player is ready: do what was last asked of it. */
  ready() {
    if (this.live) return;
    this.live = true;
    if (this.wanted) this.play();
  }
  play() {
    this.wanted = true;
    if (this.native) return this.native.play().catch(() => {});
    if (!this.live) return;
    clearTimeout(this.away);
    if (this.video.host === 'youtube' && !this.muted) this.send('unMute');
    this.send(this.video.host === 'youtube' ? 'playVideo' : 'play');
    /* If the player never says it plays (blocked messages), show it anyway. */
    clearTimeout(this.reveal);
    this.reveal = setTimeout(() => this.setAttribute('data-playing', ''), 2500);
  }
  pause() {
    this.wanted = false;
    if (this.native) return this.native.pause();
    if (!this.live) return;
    /*
      YouTube shows its title and pause button for a few seconds whenever it starts again. Out of sight it runs on
      silently for a while, so coming back soon is seamless; after that it pauses (and shows them briefly on return).
    */
    if (this.video.host === 'youtube' && this.ambient) {
      this.send('mute');
      clearTimeout(this.away);
      this.away = setTimeout(() => this.send('pauseVideo'), 45000);
    } else this.send(this.video.host === 'youtube' ? 'pauseVideo' : 'pause');
  }
  sound(on) {
    this.muted = !on;
    this.querySelector('[data-video-sound]')?.setAttribute('aria-pressed', String(on));
    if (this.native) this.native.muted = !on;
    else if (this.video.host === 'youtube') {
      this.send(on ? 'unMute' : 'mute');
      if (on) this.send('setVolume', 100);
    } else {
      this.send('setVolume', on ? 1 : 0);
      this.send('setMuted', !on);
    }
    if (on) this.play();
  }
  caption(on) {
    this.captions = on;
    this.querySelector('[data-video-captions]')?.setAttribute('aria-pressed', String(on));
    if (!this.send) return;
    if (on) {
      this.send('loadModule', 'captions');
      this.send('setOption', 'captions', 'track', { languageCode: 'en' });
    } else this.send('unloadModule', 'captions');
  }
  disconnectedCallback() {
    this.seen?.disconnect();
    clearTimeout(this.away);
    if (this.listen) removeEventListener('message', this.listen);
  }
}
if (!customElements.get('c4-video')) customElements.define('c4-video', C4Video);
if (!customElements.get('c4-product')) customElements.define('c4-product', C4Product);
/*
  The gallery. It holds where it starts as the page scrolls: the same gap under the navigation it has at the top
  (less the announcement bar once that has gone). Over the picture, an arrow either side and a dot for each picture
  near the one showing; on hover the dots open into previews (CSS). Clicking a dot or an arrow, and swiping, move
  the track; the dots follow it.
*/
const setUpGallery = (gallery) => {
  const track = gallery.querySelector('.product-media-track');
  if (!track) return;
  /*
    The previews the dots open into: small (sized for 46px, sharp on retina), fetched up front at low priority and
    decoded once the page is idle, so every dot opens into a picture that's already there, not one still loading.
  */
  const warm = () =>
    gallery.querySelectorAll('.product-media-dots img').forEach((img) => {
      img.loading = 'eager';
      img.decode?.().catch(() => {});
    });
  if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 2500 });
  else setTimeout(warm, 1200);
  const place = () => {
    const section = gallery.parentElement;
    const natural =
      section.getBoundingClientRect().top +
      scrollY +
      parseFloat(getComputedStyle(section).paddingTop);
    /* Less the announcement bar's height while it is a bar in the page (not when drawn in the header's corner, out
       of the page's flow, beside a hanging logo: page-frame.css). */
    const notice = document.querySelector('.announcement');
    const step =
      notice && !/fixed|absolute/.test(getComputedStyle(notice).position) ? notice.offsetHeight : 0;
    gallery.style.setProperty('--gallery-top', `${Math.max(0, natural - step)}px`);
  };
  place();
  addEventListener('resize', place, { passive: true });
  addEventListener('load', place, { once: true });
  const dots = [...gallery.querySelectorAll('.product-media-dots a')];
  const arrows = [...gallery.querySelectorAll('.product-media-arrow')];
  if (!dots.length) return;
  const behavior = () =>
    matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  const current = () => Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
  const go = (index) => {
    const last = dots.length - 1;
    track.scrollTo({
      left: (index < 0 ? last : index > last ? 0 : index) * track.clientWidth,
      behavior: behavior(),
    });
  };
  /* Seven dots at most, around the one showing. */
  const sync = () => {
    const index = current();
    const start = Math.min(Math.max(0, index - 3), Math.max(0, dots.length - 7));
    dots.forEach((dot, n) => {
      dot.toggleAttribute('aria-current', n === index);
      dot.toggleAttribute('data-far', n < start || n >= start + 7);
    });
    gallery.toggleAttribute('data-first', index === 0);
    gallery.toggleAttribute('data-last', index === dots.length - 1);
  };
  let queued = false;
  track.addEventListener(
    'scroll',
    () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        sync();
      });
    },
    { passive: true },
  );
  dots.forEach((dot, n) =>
    dot.addEventListener('click', (event) => {
      event.preventDefault();
      go(n);
    }),
  );
  arrows.forEach((arrow) => {
    arrow.hidden = false;
    arrow.addEventListener('click', () => go(current() + Number(arrow.dataset.mediaStep)));
  });
  sync();
};
document.querySelectorAll('.product-gallery').forEach(setUpGallery);
/* A product swapped in without a page load (another model, assets/product-build.js) sets up its own. */
document.addEventListener('c4:section-load', ({ target }) =>
  target.querySelectorAll('.product-gallery').forEach(setUpGallery),
);
